import dayjs from 'dayjs';

import i18n from '@/i18n';
import {graphqlClient} from '@/services/graphqlClient';
import type {GetEventsByDateRangeForExportQuery} from '@/services/graphqlHooks';
import {useGetEventsByDateRangeForExportQuery} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';
import eventSubTypes from '@/translation/eventSubTypes';
import eventTypes from '@/translation/eventTypes';
import {TIME_FORMATS} from '@/utils/timeFormats';

import DateRangeOptionsFields from '../../components/export-options/DateRangeOptionsFields';
import type {ExportDefinition} from '../../types/exportTypes';
import type {DateRangeOptions} from '../exportOptions';
import {
  dateRangeOptionsSchema,
  describeDateRange,
  formatExportDateTime,
  formatPersonName,
  getDateRangeDefaultOptions,
  getDateRangeFileName,
  isWithinDateRange,
} from '../exportOptions';

type EventExportRecord =
  GetEventsByDateRangeForExportQuery['getEventsByDateRange'][number] & {
    residentName: string;
    createdByName: string;
  };

export const eventsExport: ExportDefinition<
  DateRangeOptions,
  EventExportRecord
> = {
  id: 'events',
  label: i18n.t('Events'),
  permission: 'EXPORT_EVENTS',
  entitlement: 'EVENTS',
  optionsSchema: dateRangeOptionsSchema,
  OptionsFields: DateRangeOptionsFields,
  getDefaultOptions: getDateRangeDefaultOptions,
  load: async options => {
    const variables = {
      input: {
        startDate: dayjs(options.startDate)
          .subtract(1, 'day')
          .format(TIME_FORMATS.awsDate),
        endDate: dayjs(options.endDate)
          .add(1, 'day')
          .format(TIME_FORMATS.awsDate),
      },
    };
    const {getEventsByDateRange, getAllCurrentResidents, getAllUsers} =
      await queryClient.fetchQuery({
        queryKey: useGetEventsByDateRangeForExportQuery.getKey(variables),
        queryFn: useGetEventsByDateRangeForExportQuery.fetcher(
          graphqlClient,
          variables
        ),
      });
    const residents = new Map(
      getAllCurrentResidents.map(resident => [resident.id, resident])
    );
    const users = new Map(getAllUsers.map(user => [user.userName, user]));

    return getEventsByDateRange
      .filter(event => isWithinDateRange(event.date, options))
      .flatMap(event => {
        const resident = event.pairingId
          ? residents.get(event.pairingId)
          : undefined;
        if (event.pairingId && !resident) return [];

        return [
          {
            ...event,
            residentName: formatPersonName(resident?.name),
            createdByName:
              formatPersonName(users.get(event.createdBy)?.name) ||
              event.createdBy,
          },
        ];
      })
      .sort((eventA, eventB) => dayjs(eventA.date).diff(eventB.date));
  },
  columns: [
    {header: i18n.t('Date'), value: event => formatExportDateTime(event.date)},
    {
      header: i18n.t('Resident'),
      filterable: true,
      value: event => event.residentName,
    },
    {
      header: i18n.t('Type'),
      filterable: true,
      value: event => eventTypes[event.type],
    },
    {
      header: i18n.t('Sub type'),
      filterable: true,
      value: event => eventSubTypes[event.eventSubType],
    },
    {header: i18n.t('Content'), value: event => event.content},
    {
      header: i18n.t('Created by'),
      filterable: true,
      value: event => event.createdByName,
    },
  ],
  describeOptions: describeDateRange,
  getFileName: options => getDateRangeFileName('events', options),
};
